import * as React from 'react';
import { useMemo } from 'react';

import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import type { DiscoveryCollection } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import AutoComplete from 'bundles/search-common/components/searchbar/AutoComplete';
import type { SearchBarLocation } from 'bundles/search-common/constants';
import {
  REQUEST_ORIGIN_PAGE_TYPE,
  REQUEST_ORIGIN_SEGMENT_TYPE,
  SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID,
} from 'bundles/search-common/constants';
import { AUTOCOMPLETE_SEARCH_CONFIG } from 'bundles/search-common/constants/autocomplete';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import type { SearchConfig } from 'bundles/search-common/providers/searchTypes';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';

import 'css!./__styles__/AutoComplete';

export type Props = {
  isSearchPage: boolean;
  pageLocation?: SearchBarLocation;
  searchIsOpen?: boolean;
  collectionRecommendations?: DiscoveryCollection | null;
  shouldFocusSearch?: boolean;
  onSearchFocus?: () => void;
  hideMobileSearchPage?: () => void;
  enableOneStepSearch?: boolean;
  shouldShowExposedSearchAndReg?: boolean;
  startingQuery?: string;
};

const AutoCompleteWrapper = (props: Props) => {
  const track = useRetracked();
  const location = useLocation();
  const queryFromUrl = inferQueryFromParams(location.query?.query || '');

  const searchConfigs = useMemo(() => {
    const isSearchPage = location.pathname === '/search';
    if (isSearchPage) {
      return AUTOCOMPLETE_SEARCH_CONFIG.map((config): SearchConfig => {
        if (config.id === SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID) {
          return {
            ...config,
            requestOrigin: {
              pageType: REQUEST_ORIGIN_PAGE_TYPE.SERP,
              segmentType: REQUEST_ORIGIN_SEGMENT_TYPE.CONSUMER,
            },
          };
        }
        return config;
      });
    }
    return AUTOCOMPLETE_SEARCH_CONFIG;
  }, [location.pathname]);

  return (
    <SearchProvider searchConfigs={searchConfigs}>
      <AutoComplete track={track} query={queryFromUrl || props.startingQuery || ''} {...props} />
    </SearchProvider>
  );
};

export default AutoCompleteWrapper;
